import { Injectable, inject } from '@angular/core'
import { HttpClient } from '@angular/common/http'
import { environment } from '../../environments/environment'
import {
  EMPTY,
  Observable,
  Subject,
  Subscriber,
  catchError,
  finalize,
  mergeMap,
  takeUntil,
  tap,
  timeout,
} from 'rxjs'

type ImageRequest = {
  url: string
  result: Subscriber<Blob>
  cancel: Subject<void>
}

@Injectable({ providedIn: 'root' })
export class ProductImageService {
  private http = inject(HttpClient)
  private requests = new Subject<ImageRequest>()

  /**
   * Limits image downloads to four so navigation and actions can still load.
   * @returns A service with a shared queue for product image downloads.
   */
  constructor() {
    this.requests
      .pipe(
        mergeMap((request) => {
          if (request.result.closed) return EMPTY
          return this.http.get(request.url, { responseType: 'blob' }).pipe(
            // Give the API's 15-second image timeout a little room to finish.
            timeout(16000),
            takeUntil(request.cancel),
            tap((blob) => request.result.next(blob)),
            catchError((error: unknown) => {
              request.result.error(error)
              return EMPTY
            }),
            finalize(() => request.result.complete()),
          )
        }, 4),
      )
      .subscribe()
  }

  /**
   * Loads a protected photo, optionally selecting its card or detail size.
   * Unsubscribing cancels queued or active work.
   * @param picture Image address returned by the API.
   * @param size Overrides the size in that address when supplied.
   * @returns Image bytes, or an error for an invalid address or failed download.
   */
  load(picture: string, size?: 'card' | 'detail'): Observable<Blob> {
    return new Observable((result) => {
      let url: URL
      try {
        const api = new URL(environment.baseUrl, window.location.origin)
        url = new URL(picture, api)
        // The interceptor adds our token, so only request our own image endpoint.
        if (
          url.origin !== api.origin ||
          !/^\/api\/products\/[^/]+\/image$/.test(url.pathname)
        )
          throw new Error('Invalid product image URL')
        if (size) url.searchParams.set('size', size)
      } catch (error) {
        // A malformed address should use the same fallback as a failed download.
        result.error(error)
        return
      }
      const cancel = new Subject<void>()
      this.requests.next({ url: url.href, result, cancel })
      return () => {
        cancel.next()
        cancel.complete()
      }
    })
  }
}
